import * as React from "react";
import { useRef } from "react";
import { XIcon } from "lucide-react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export type StrategyPineDetailDialogSize = "compact" | "wide";

/** Match dialog overlay/content duration so callers can defer state resets until exit finishes. */
export const PINE_DETAIL_DIALOG_CLOSE_MS = 200;

export type StrategyPineDetailDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** compact = checklist / summary; wide = Pinescript code panels */
  size?: StrategyPineDetailDialogSize;
  /** Optional width/layout overrides merged onto dialog content (e.g. `sm:max-w-md`). */
  contentClassName?: string;
  /** Optional overrides for the scrollable body region (e.g. `overflow-visible flex-none`). */
  bodyClassName?: string;
  /** Extra attributes forwarded to dialog content (e.g. layout markers). */
  contentProps?: Omit<
    React.ComponentProps<typeof DialogContent>,
    "children" | "className" | "showCloseButton"
  > &
    Record<`data-${string}`, string | undefined>;
  /** Footer leading control (defaults to Cancel). Ignored when `showDismissButton` is false. */
  dismissLabel?: string;
  /** When false, footer leading Close/Cancel is hidden. Defaults to true. */
  showDismissButton?: boolean;
  /** When false, hides the top-right X dismiss control. Defaults to true. */
  showCornerClose?: boolean;
  /** When false, hides the visible dialog title/description (keeps sr-only title for a11y). */
  showHeader?: boolean;
  /** Keep dialog body mounted while closed (e.g. in-flight compose API). */
  keepContentMounted?: boolean;
};

type PineDetailPresentation = {
  title: string;
  description?: React.ReactNode;
  footer?: React.ReactNode;
  size: StrategyPineDetailDialogSize;
  dismissLabel: string;
  showDismissButton: boolean;
  showCornerClose: boolean;
  showHeader: boolean;
};

export function StrategyPineDetailDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = "compact",
  contentClassName,
  bodyClassName,
  contentProps,
  dismissLabel = "Cancel",
  showDismissButton = true,
  showCornerClose = true,
  showHeader = true,
  keepContentMounted = false,
}: StrategyPineDetailDialogProps): React.ReactElement {
  const presentationRef = useRef<PineDetailPresentation>({
    title,
    description,
    footer,
    size,
    dismissLabel,
    showDismissButton,
    showCornerClose,
    showHeader,
  });

  if (open) {
    presentationRef.current = {
      title,
      description,
      footer,
      size,
      dismissLabel,
      showDismissButton,
      showCornerClose,
      showHeader,
    };
  }

  const presentation = open
    ? { title, description, footer, size, dismissLabel, showDismissButton, showCornerClose, showHeader }
    : presentationRef.current;
  const layoutSize = presentation.size;
  const showFooter = presentation.showDismissButton || Boolean(presentation.footer);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        forceMount={keepContentMounted || undefined}
        {...contentProps}
        data-strategy-pine-detail-dialog=""
        data-strategy-pine-detail-dialog-size={layoutSize}
        className={cn(
          "flex max-h-[min(90vh,48rem)] w-full flex-col overflow-hidden duration-200 data-closed:duration-200",
          layoutSize === "wide" ? "sm:max-w-6xl" : "sm:max-w-2xl",
          !open && keepContentMounted && "pointer-events-none invisible !hidden",
          contentClassName
        )}
      >
        {presentation.showCornerClose ? (
          <DialogClose asChild>
            <button
              type="button"
              data-strategy-pine-detail-dismiss=""
              aria-label="Close"
              className="absolute top-4 right-4 inline-flex size-8 items-center justify-center rounded-md text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-white dark:hover:bg-white/5 dark:hover:text-white"
            >
              <XIcon className="size-4" />
            </button>
          </DialogClose>
        ) : null}

        {presentation.showHeader ? (
          <DialogHeader className="shrink-0">
            <DialogTitle className="font-[family-name:var(--font-primary)] text-base font-normal text-[color:var(--brand-tropical-surf)]">
              {presentation.title}
            </DialogTitle>
            {presentation.description ? (
              <DialogDescription className="font-[family-name:var(--font-primary)] font-normal text-gray-500 dark:text-white">
                {presentation.description}
              </DialogDescription>
            ) : null}
          </DialogHeader>
        ) : (
          <DialogTitle className="sr-only">{presentation.title}</DialogTitle>
        )}

        <div
          data-strategy-pine-detail-dialog-body=""
          className={cn(
            "box-border min-h-0 flex-1 overflow-y-auto overscroll-contain px-1 pt-1 pb-2 [scrollbar-gutter:stable]",
            bodyClassName
          )}
        >
          {children}
        </div>

        {showFooter ? (
          <DialogFooter className="shrink-0 justify-end sm:justify-end">
            <div
              data-strategy-pine-detail-dialog-footer=""
              className={cn(
                "flex max-w-full flex-wrap items-center gap-2",
                presentation.showDismissButton ? "justify-end" : "w-full justify-between"
              )}
            >
              {presentation.showDismissButton ? (
                <DialogClose asChild>
                  <button type="button" data-strategy-pine-detail-cancel="" data-font="primary">
                    {presentation.dismissLabel}
                  </button>
                </DialogClose>
              ) : null}
              {presentation.footer}
            </div>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
